/**
 * isiXhosa: Flows and chatbots
 *
 * What this file covers: The chatbot flow builder, its steps, AI answers, Build with AI, and the chatbots
 * themselves.
 *
 * Tone: Practical and a little technical, never heavy with jargon. Step names are short actions (Ask a
 * question, Book a time). Explanations say what the step does for the client.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const flows: Catalog = {
  'flows.title': 'Ukuhamba',
  'flows.builder.title': 'Umakhi',
  'flows.builder.saveFailed': 'Ukugcina kusilele',
  'flows.builder.unsaved': 'Utshintsho olungagcinwanga',
  'flows.builder.canvasTab': 'Ikhanvasi',
  'flows.builder.palette': 'Iindawo',
  'flows.builder.properties': 'Iimpawu',
  'flows.builder.group.start': 'Qalisa',
  'flows.builder.group.steps': 'Amanyathelo',
  'flows.builder.node.trigger': 'Isiqalisi',
  'flows.builder.node.reply': 'Thumela impendulo',
  'flows.builder.node.handoff': 'Dlulisela',
  'flows.builder.field.keyword': 'Igama elingundoqo',
  'flows.builder.field.body': 'Umyalezo',
  'flows.drawer.definition': 'Inkcazo',
  'flows.drawer.nameRequired': 'Igama liyafuneka.',
  'flows.empty': 'Akukho kuhamba okwangoku: yenza okunye ukuze uzenzekelise iimpendulo.',
  'flows.loadError': 'Ayikwazanga ukulayisha ukuhamba.',
  'flows.modal.delete.confirm': 'Cima ukuhamba',
  'flows.modal.delete.title': 'Cima ukuhamba?',
  'flows.runs.empty': 'Akukho zibaliso okwangoku kolu hambo.',
  'flows.view.backToAll': 'Konke ukuhamba',
  'flows.view.lifecycle.activateFlow': 'Yenza ukuhamba kusebenze',
  'flows.view.lifecycle.activation': 'Ukwenza kusebenze',
  'flows.view.lifecycle.deactivateFlow': 'Yeka ukuhamba',
  'flows.view.lifecycle.deleteFailed': 'Ayikwazanga ukucima ukuhamba.',
  'flows.view.lifecycle.updateFailed': 'Ayikwazanga ukuhlaziya ukuhamba.',
  'flows.view.overview.flowId': 'I-ID yokuhamba',
  'flows.view.overview.title': 'Iinkcukacha zokuhamba',
  'flows.view.runs.columns.contact': 'Umqhagamshelwano',
  'flows.view.runs.columns.duration': 'Ubude bexesha',
  'flows.view.runs.columns.error': 'Impazamo',
  'flows.view.runs.title': 'Izibaliso zakutshanje',
  'flows.view.tabs.runs': 'Izibaliso',
  'flows.actions.create': 'Yenza umsinga',
  'flows.showingOf': 'Kuboniswa {{count}} kwezingu-{{total}}',
  'flows.node.trigger': 'Isiqalisi',
  'flows.node.reply': 'Thumela impendulo',
  'flows.builder.save': 'Gcina utshintsho',
  'flows.connect.metaChannelLive': 'iphendula nalapha, xa nje iphepha liqhagamshelwe.',
  'flows.connect.metaChannelWhere': 'Qhagamshela iphepha le-Facebook okanye iakhawunti ye-Instagram kwi-',
  'bots.switcher.label': 'I-chatbot',
  'bots.switcher.hint': 'Ujonge umsebenzi wale chatbot.',
  'bots.switcher.flowsHint': 'Ukuhamba okungezantsi kokwale chatbot. Ukuhamba okutsha kongezwa kuyo.',
  'bots.switcher.knowledgeHint': 'I-chatbot nganye ineempendulo zayo ezizenzekelayo. Amaxwebhu abelwana ngawo zonke.',
  'bots.switcher.defaultOption': '{{name}} (emiselweyo)',
  'bots.channels.title': 'Apho le chatbot iphendula khona',
  'bots.channels.intro': 'Khetha iinombolo, iincoko zewebhusayithi neeakhawunti le chatbot ekufuneka iphendule kuzo. Nganye iphendulwa yi-chatbot enye, ngoko ukuyikhetha apha kuyisusa kwi-chatbot ebinayo.',
  'bots.channels.introDefault': 'Le yi-chatbot yakho emiselweyo: iphendula naphi na apho kungakhethwanga enye i-chatbot. Ukunika enye i-chatbot inombolo okanye incoko, vula loo chatbot uze uyikhethe apho.',
  'bots.channels.noFlows': '{{name}} ayikabi nako ukuhamba, ngoko abathengi ababhala apha abafumani mpendulo. Yakha enye ku:',
  'bots.channels.none': 'Awukaqhagamshelisi zinombolo, zincoko zewebhusayithi okanye iiakhawunti. Qala ku:',
  'bots.channels.kind.whatsapp': 'WhatsApp',
  'bots.channels.kind.web': 'Incoko yewebhusayithi',
  'bots.channels.kind.messenger': 'Messenger',
  'bots.channels.kind.instagram': 'Instagram',
  'bots.channels.mineByDefault': 'Iphendula apha kuba akukho yimbi i-chatbot ekhethiweyo.',
  'bots.channels.answeredBy': 'Ngoku iphendulwa ngu-{{name}}.',
  'bots.channels.moved': 'I-{{account}} ngoku iphendulwa ngu-{{name}}.',
  'bots.channels.returned': 'I-{{account}} ibuyele ku-{{name}}.',
  'bots.channels.confirm.title': 'Yihambisele kule chatbot?',
  'bots.channels.confirm.body': 'I-{{account}} iphendulwa ngu-{{from}} namhlanje. Ukusuka kumyalezo olandelayo iza kuphendulwa ngu-{{to}}. Incoko esele iqhubeka iza kugqitywa ngu-{{from}}.',
  'bots.channels.confirm.action': 'Yihambise',
  'bots.actions.title': 'Emiselweyo nokucima',
  'bots.actions.isDefault': 'Le yi-chatbot yakho emiselweyo. Iphendula kuzo zonke iinombolo, iincoko zewebhusayithi neeakhawunti ezinganikwanga enye i-chatbot, ngoko ayinakucinywa. Ukuze uyicime, qala wenze enye i-chatbot ibe yemiselweyo.',
  'bots.actions.defaultHint': 'I-chatbot emiselweyo iphendula kuzo zonke iinombolo, iincoko zewebhusayithi neeakhawunti ezinganikwanga enye i-chatbot.',
  'bots.actions.madeDefault': 'U-{{name}} ngoku yi-chatbot yakho emiselweyo.',
  'bots.actions.deleted': 'U-{{name}} ucinyiwe.',
  'bots.actions.makeDefault.title': 'Yenza u-{{name}} abe ngomiselweyo?',
  'bots.actions.makeDefault.body': 'U-{{name}} uza kuphendula naphi na apho kungakhethwanga i-chatbot, ukusuka kumyalezo olandelayo. Ukuhamba kuhlala ne-chatbot okukokwayo.',
  'bots.actions.makeDefault.action': 'Yenza emiselweyo',
  'bots.actions.delete.title': 'Cima u-{{name}}?',
  'bots.actions.delete.body': 'Le chatbot ayinako ukuhamba kwaye ayiphenduli ndawo, ngoko akukho nto yimbi itshintshayo. Iincoko zangaphambili zigcina imbali yazo.',
  'bots.actions.delete.action': 'Cima i-chatbot',
  'bots.actions.delete.hint': 'Ukucima kususa igama neesetingi zale chatbot. Iincoko zangaphambili zigcina imbali yazo.',
  'bots.actions.delete.hintInUse': 'Le chatbot inokuhamba okanye iphendula kwi-akhawunti. Uza kubuzwa ukuba yeyiphi i-chatbot eza kukuthatha.',
  'bots.actions.move.body': 'U-{{name}} usenomsebenzi: ukuhamba {{flows}}, iiakhawunti {{accounts}}. Khetha i-chatbot eza kukuthatha. Kuhanjiswa ngenyathelo elinye nokucima.',
  'bots.actions.move.label': 'Hambisa yonke into ku',
  'bots.actions.move.hint': 'Loo chatbot iza kuqhuba oku kuhamba ize iphendule kwezi akhawunti ukusuka kumyalezo olandelayo.',
  'bots.actions.move.action': 'Hambisa uze ucime',
  'bots.add.title': 'Yongeza i-chatbot',
  'bots.add.intro': 'I-chatbot inegama layo, indlela yayo yokuthetha nokuhamba kwayo. Emva kokuyongeza, khetha apho iphendula khona uze wakhe ukuhamba kwayo.',
  'bots.add.copyFrom': 'Qala ukusuka ku',
  'bots.add.copyFromHint': 'Kukopa ithoni, ulwimi, iimpendulo zencoko emfutshane, uluhlu lwamagama nezaziso. Ukuhamba neeakhawunti akukotshwa.',
  'bots.add.copyNothing': 'I-chatbot engenanto',
  'bots.add.action': 'Yongeza i-chatbot',
  'bots.add.done': 'U-{{name}} wongeziwe. Khetha apho aphendula khona, uze wakhe ukuhamba kwakhe.',
  'bots.add.cardTitle': 'Enye i-chatbot',
  'bots.add.cardBody': 'Phatha uphawu lwesibini, ulwimi okanye isebe ngegama lalo, ukuhamba neenombolo zalo. I-chatbot enye inokuphendula kwiinombolo ezininzi, okanye inombolo nganye ibe neyayo.',
  'bots.add.atLimit': 'Ii-chatbot ezibandakanyiweyo kwi-{{plan}}: {{count}}. Nyukela kwisicwangciso esikhulu ukuze wongeze enye.',
  'bots.add.seePlans': 'Bona izicwangciso',
  'bots.list.title': 'Ii-chatbot zakho',
  'bots.list.intro': 'I-chatbot nganye inegama layo, ukuhamba kwayo neenombolo neencoko ephendula kuzo. Vula enye ukuze uyitshintshe.',
  'bots.list.default': 'Emiselweyo',
  'bots.list.answers': 'Iinombolo neencoko eziqhagamshelweyo: {{count}}',
  'bots.list.answersDefault': 'Iphendula naphi na apho kungakhethwanga enye i-chatbot',
  'bots.list.flows': 'Ukuhamba: {{count}}',
  'bots.loadError': 'Ii-chatbot zakho azikwazanga ukulayishwa. Hlaziya iphepha ukuze uzame kwakhona.',
  'bots.backToList': 'Buyela kwii-chatbot zakho',
  'bots.notFound': 'Loo chatbot ayisekho.',
  'bots.flow.label': 'I-chatbot',
  'bots.flow.hint': 'Yile chatbot kuphela eqhuba oku kuhamba. Ukukuhambisa kuqala kumyalezo olandelayo.',
  'bots.header.count': '{{current}}/{{allowance}} · {{plan}}',
  'bots.header.countUnlimited': '{{current}} · {{plan}}, akukho mda',
  'bots.header.single': 'U-{{name}} uphendula kuzo zonke iinombolo zakho neencoko. Yongeza i-chatbot ukuze uphathe uphawu lwesibini, ulwimi okanye isebe ngegama lalo, ukuhamba neenombolo zalo.'
};

export default flows;
